import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { derivePostTitle } from '../../../helpers/derivePostTitle';
import { getRelativeTime } from '../../../helpers/getRelativeTime';
import { useMyPendingRewrites } from '../../../hooks/useMyPendingRewrites';
import type { TextBlock } from '../../../interfaces/textEditor.interface';
import './pending-rewrite-bell.scss';

interface Props {
  collapsed: boolean;
}

export function PendingRewriteBell({ collapsed }: Props) {
  const navigate = useNavigate();
  const { posts, count } = useMyPendingRewrites();

  if (count === 0) return null;

  const trigger = (
    <button
      type="button"
      className={`PENDING__REWRITE__BELL ${
        collapsed
          ? 'PENDING__REWRITE__BELL--collapsed'
          : 'PENDING__REWRITE__BELL--expanded'
      }`}
      aria-label={`Posts awaiting rewrite (${count})`}
    >
      <span className="PENDING__REWRITE__BELL__ICON">
        <Bell size={20} aria-hidden="true" />
        {collapsed && (
          <span className="PENDING__REWRITE__BELL__BADGE PENDING__REWRITE__BELL__BADGE--corner">
            {count}
          </span>
        )}
      </span>
      {!collapsed && (
        <>
          <span className="PENDING__REWRITE__BELL__LABEL">
            Awaiting rewrite
          </span>
          <span className="PENDING__REWRITE__BELL__BADGE PENDING__REWRITE__BELL__BADGE--end">
            {count}
          </span>
        </>
      )}
    </button>
  );

  return (
    <DropdownMenu.Root>
      <Tooltip.Root key={collapsed ? 'c' : 'e'}>
        <Tooltip.Trigger asChild>
          <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
        </Tooltip.Trigger>
        {collapsed && (
          <Tooltip.Portal>
            <Tooltip.Content
              side="right"
              sideOffset={8}
              className="NAVIGATION__ITEM__TOOLTIP"
            >
              Awaiting rewrite ({count})
            </Tooltip.Content>
          </Tooltip.Portal>
        )}
      </Tooltip.Root>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="right"
          align="start"
          sideOffset={8}
          className="PENDING__REWRITE__BELL__MENU"
        >
          <div className="PENDING__REWRITE__BELL__MENU__HEAD">
            <span>Posts awaiting rewrite</span>
            <span className="PENDING__REWRITE__BELL__MENU__PILL">{count}</span>
          </div>
          {posts.map((post) => (
            <DropdownMenu.Item
              key={post.id}
              className="PENDING__REWRITE__BELL__ROW"
              onSelect={() => navigate(`/post/${post.id}/edit-rewrite`)}
            >
              <p className="PENDING__REWRITE__BELL__ROW__TITLE">
                {derivePostTitle(
                  post.textBlocks as unknown as TextBlock[] | undefined,
                )}
              </p>
              {post.moderation?.headline && (
                <p className="PENDING__REWRITE__BELL__ROW__VERDICT">
                  <strong>{post.moderation.headline}</strong>
                  {post.moderation.body ? ` — ${post.moderation.body}` : ''}
                </p>
              )}
              <span className="PENDING__REWRITE__BELL__ROW__CTA">
                Edit now →
              </span>
              {post.moderation?.moderatorUsername && (
                <p className="PENDING__REWRITE__BELL__ROW__META">
                  reviewed by @{post.moderation.moderatorUsername}
                  {post.moderation?.resolvedAt
                    ? ` · ${getRelativeTime(post.moderation.resolvedAt)}`
                    : ''}
                </p>
              )}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
